<template>
  <view-wrapper
    name="Order"
    path="myorders"
    :loading="!!!order.orderData"
    loading-text="Retrieving order"
  >
    <wrapper class="mb-4">
      <g-input
        v-model="orderReference"
        label-placeholder="Order reference"
        disabled
      />
    </wrapper>

    <wrapper class="mb-4" :focus="true">
      <div class="flex justify-between">
        <p>Order data</p>
        <p
          class="font-bold underline cursor-pointer"
          @click="orderDataExpanded = !orderDataExpanded"
        >
          {{ orderDataExpanded ? "Collapse" : "Expand" }}
        </p>
      </div>
      <div class="flex justify-end">
        <p
          class="font-bold underline cursor-pointer"
          @click="showAllOrderData = !showAllOrderData"
        >
          {{ showAllOrderData ? "Show only editable" : "Show all" }}
        </p>
      </div>
      <div v-if="order.orderData" v-show="orderDataExpanded">
        <div
          v-for="(data, index) in orderData"
          :key="`order-data--${index}`"
          class="grid grid-cols-5 gap-4"
        >
          <g-input
            :id="`order-key--${index}`"
            type="text"
            label-placeholder="Key"
            v-model="data.key"
            class="col-span-1"
            disabled
          />
          <g-input
            :id="`order-data--${index}`"
            type="text"
            label-placeholder="Data"
            v-model="data.data"
            :readonly="!data.editable"
            :editable="data.editable"
            @input="updateEditableOrderData(data.key, $event)"
            :is-expandable="true"
            class="col-span-4"
          />
        </div>
      </div>
    </wrapper>

    <h2 class="mt-6">Articles</h2>
    <wrapper class="mb-4" :focus="true">
      <div class="flex justify-between">
        <p>Article data</p>
        <p
          class="font-bold underline cursor-pointer"
          @click="articleDataExpanded = !articleDataExpanded"
        >
          {{ articleDataExpanded ? "Collapse" : "Expand" }}
        </p>
      </div>

      <div v-show="articleDataExpanded">
        <div class="flex justify-between">
          <p>&nbsp;</p>
          <p
            class="font-bold underline cursor-pointer"
            @click="showAllArticleData = !showAllArticleData"
          >
            {{ showAllArticleData ? "Show only editable" : "Show all" }}
          </p>
        </div>
      </div>

      <wrapper
        class="mb-4"
        v-show="articleDataExpanded"
        v-for="(article, articleIndex) in articles"
        :key="`article-data--${articleIndex}`"
      >
        <p>{{ article.name }}</p>

        <div v-if="article.articleData" class="mb-8">
          <div
            v-for="(data, index) in article.articleData"
            v-show="data.editable || showAllArticleData"
            :key="`editable-article-data--${articleIndex}-${index}`"
            class="grid grid-cols-5 gap-4"
          >
            <g-input
              :id="`editable-key--${articleIndex}-${index}`"
              type="text"
              label-placeholder="Key"
              v-model="data.key"
              class="col-span-1 edit"
              disabled
            />
            <g-input
              :id="`editable-data--${articleIndex}-${index}`"
              :type="data.propertyType"
              label-placeholder=""
              v-model="data.data"
              @input="updateEditableData(articleIndex, data.key, $event)"
              :readonly="!data.editable"
              :editable="data.editable"
              :maxlength="findMaxLength(data)"
              :is-expandable="true"
              class="col-span-4"
            />
          </div>
        </div>
      </wrapper>
    </wrapper>

    <h2 class="mt-6">Print Order</h2>
    <wrapper class="flex flex-col mb-5">
      <div class="flex justify-end mb-4" v-if="articles.length > 1">
        <ArticleSortBy :options="sortOptions" v-model="sortState" />
      </div>
      <print-article
        v-for="article in sortedArticles"
        :key="`print-article--${article.articleId}`"
        :article="article"
        :labels="article.printLabels"
        :orderData="order.orderData"
        :orderId="order.orderId"
        @updateLabelQuantity="updateLabelQuantity"
      />
      <g-button
        :disabled="!quantityAvailable"
        class="ml-auto primary"
        @click="printerSelectOpen = true"
        >Select printers</g-button
      >
      <printer-select
        :key="`printer--select--${componentKey}`"
        :order="order"
        v-if="printerSelectOpen"
        @close="printerSelectOpen = false"
        :labels="labelsToPrint()"
        :printers="printers"
        :enable-print-to-group="enablePrintToGroup"
      />
    </wrapper>

    <FeatureZone features="integrationWebHooksEnabled">
      <wrapper class="mb-4">
        <h2 class="mt-6">Work flow</h2>
        <g-input
          v-model="orderStatus"
          label-placeholder="Order status"
          disabled
        />
        <g-button
          v-if="orderStatus !== 'Completed'"
          class="mb-2 button big primary white"
          @click="markOrderCompleted"
          >Set as completed</g-button
        >
      </wrapper>
    </FeatureZone>

    <modal
      v-if="errorMessage"
      type="error"
      heading="Error"
      :description="errorMessage"
      @close="errorMessage = null"
    >
    </modal>
  </view-wrapper>
</template>
<script>
import Modal from "../../components/Modal";
import GInput from "../../components/forms/Input";
import PrintArticle from "../../components/PrintArticle";
import PrinterSelect from "../../components/PrinterSelect";
import GButton from "../../components/forms/Button";
import FeatureZone from "../../components/FeatureZone";
import ArticleSortBy from "../../components/ArticleSortBy.vue";

import { getVisibleVariables } from "../../utils/labelHelper";
import { getArticleDataVariables } from "../../utils/articleDataHelper";
import {
  ARTICLE_NAME_SORT_KEY,
  SORT_ASCENDING,
  buildSortOptions,
  sortArticles,
  getArticleDataKeys,
  getArticleDataValue,
} from "../../utils/articleSortHelper";

export default {
  components: {
    Modal,
    GInput,
    PrintArticle,
    PrinterSelect,
    GButton,
    FeatureZone,
    ArticleSortBy,
  },
  data: () => ({
    order: {},
    quantityAvailable: false,
    printerSelectOpen: false,
    orderDataExpanded: true,
    articleDataExpanded: true,
    showAllArticleData: false,
    componentKey: 0, // To force update for modal,
    showAllOrderData: false,
    errorMessage: null,
    sortState: {
      sortKey: ARTICLE_NAME_SORT_KEY,
      direction: SORT_ASCENDING,
    },
  }),
  methods: {
    async getData() {
      const response = await this.$store.$api.get(
        `/api/orders/print/${this.$route.params.id}`
      );
      this.order = response.data.data;

      var allLabelVars = this.order.labels.flatMap(x => x.variables);
      var visibleVariables = getVisibleVariables({ variables: allLabelVars });

      this.order.orderData = this.order.orderData.map(od => ({
        ...od,
        propertyType:
          (visibleVariables.find(v => v.name === od.key) &&
            visibleVariables.find(v => v.name === od.key).propertyType) ||
          null,
        editable: this.isVariableEditable(od.key, allLabelVars),
      }));

      this.articles.forEach(article => {
        article.printLabels = this.order.labels.map(l => ({ ...l }));
      });

      this.order.articles = this.order.articles.map(a => {
        const allData = a.editableData.concat(a.staticData);
        const artData = getArticleDataVariables(
          this.order.labels,
          this.order.orderData,
          allData
        );
        return {
          ...a,
          quantity: "",
          articleData: artData,
          editableData: artData.filter(a => a.editable),
        };
      });

      this.componentKey++;
    },
    updateEditableOrderData(key, data) {
      const variable = this.order.orderData.find(
        od => od.key.toLowerCase() === key.toLowerCase()
      );
      if (variable) {
        variable.data = data;
        variable.changed = true;
        for (var articleIndex in this.order.articles) {
          this.updateEditableData(articleIndex, key, data, true);
        }
      }
    },
    updateEditableData(index, key, data, appendIfMissing) {
      const variable = this.order.articles[index].editableData.find(
        ed => ed.key === key
      );
      if (variable) {
        variable.data = data;
        variable.changed = true;
      } else if (appendIfMissing) {
        this.order.articles[index].editableData.push({
          key: key,
          data: data,
          changed: true,
        });
      }
    },
    findMaxLength(d) {
      if (d && d.propertySize) {
        return d.propertySize;
      }
      return 2000;
    },
    updateLabelQuantity({ article, label, quantity }) {
      const orderArticle = this.order.articles.find(
        a => a.articleId === article.articleId
      );

      orderArticle.printLabels.find(
        l => l.labelId === label.labelId
      ).quantity = quantity;
      this.quantityAvailable = orderArticle.printLabels.some(
        x => x.quantity > 0
      );
      this.componentKey++;
    },
    isVariableEditable(key, variables) {
      return variables.filter(x => x.name === key).some(x => x.editable);
    },
    labelsToPrint() {
      const labels = [];
      this.sortedArticles.forEach(a => {
        a.printLabels.forEach(l => {
          if (l.quantity && l.quantity > 0) {
            l.article = a;
            labels.push({ ...l });
          }
        });
      });

      return labels;
    },
    async markOrderCompleted() {
      const response = await this.$store.$api.post(
        `/api/orders/${this.order.orderId}/completed`
      );

      this.errorMessage =
        response && response.data && response.data.errorReason;
      if (!this.errorMessage) await this.getData();
    },
  },
  computed: {
    orderReference() {
      return this.order.orderReference ? this.order.orderReference : "";
    },
    orderStatus() {
      return this.order.orderStatus ? this.order.orderStatus : "";
    },
    articles() {
      return this.order.articles ? this.order.articles : [];
    },
    sortOptions() {
      return buildSortOptions(this.articles, article =>
        getArticleDataKeys(article)
      );
    },
    sortedArticles() {
      return sortArticles(
        this.articles,
        this.sortState.sortKey,
        this.sortState.direction,
        (article, key) => getArticleDataValue(article, key)
      );
    },
    printers() {
      return this.$store.getters["PRINTERS/INSTALLED_PRINTERS"];
    },
    enablePrintToGroup() {
      // Backend returns enablePrintToGroup on order payload for this view.
      if (this.order && this.order.enablePrintToGroup === true) {
        return true;
      }

      // Fallback for payloads that expose the flag on labels.
      return this.labelsToPrint().some(label => label.enablePrintToGroup);
    },
    orderData() {
      return this.showAllOrderData
        ? this.order.orderData
        : this.order.orderData.filter(x => x.editable);
    },
  },
  async mounted() {
    await this.getData();
    if (this.printers.length === 0) {
      await this.$store.dispatch("PRINTERS/GET_INSTALLED_PRINTERS");
    }
  },
};
</script>
